import { graphFetch, type GraphRequestOptions } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { isItemDone, putItem } from "../../storage/idb.ts";
import { assetHash, assetDiskPath, assetRelativePath } from "./paths.ts";
import type { RunLogger } from "../../export/run.ts";

/**
 * Inline images in Teams messages (ARCHITECTURE.md §8, "Teams inline images").
 *
 * hostedContents URLs require a bearer token — they are not public — but they
 * are covered by Chat.Read, so no extra scope or consent is needed. Each is
 * fetched and written to assets/, then the sanitizer rewrites the message
 * body's <img src> to the local path.
 */

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/bmp": ".bmp",
    "image/svg+xml": ".svg"
};

const HOSTED_CONTENT_PATTERN = /\/hostedContents\/[^/]+\/\$value/i;

/**
 * Finds <img> src values that reference Teams hosted content, in the exact
 * string form they appear in the raw (unsanitized) body — that string is later
 * used as the sanitizer's lookup key, so it must match what the sanitizer sees
 * when it re-parses the same content.
 *
 * The template element is used purely as a read-only parser here: nothing is
 * ever attached to a live document, so nothing executes or loads.
 */
export function findHostedImageSrcs(bodyHtml: string): string[] {
    const template = document.createElement("template");
    template.innerHTML = bodyHtml;

    const found: string[] = [];
    for (const img of Array.from(template.content.querySelectorAll("img"))) {
        const src = img.getAttribute("src");
        if (src && HOSTED_CONTENT_PATTERN.test(src)) {
            found.push(src);
        }
    }
    return found;
}

export interface FetchedAssets {
    /** Raw src string (as found in the message body) -> path relative to index.html. */
    urlMap: Map<string, string>;
    /**
     * Bytes actually written by this call. Zero for assets already on disk from
     * an earlier session, so a resume doesn't inflate the reported total with
     * work it didn't redo.
     */
    bytes: number;
}

/**
 * Fetches every hosted-content image referenced in `bodyHtml` and writes it to
 * `teams/<key>/assets/`. Each asset is its own resumable item, keyed by its
 * content hash — re-running after an interruption skips ones already written.
 */
export async function fetchHostedImages(params: {
    runId: string;
    chatId: string;
    messageId: string;
    chatKey: string;
    bodyHtml: string;
    root: FileSystemDirectoryHandle;
    scopes: readonly string[];
    logger: RunLogger;
}): Promise<FetchedAssets> {
    const { runId, chatId, messageId, chatKey, bodyHtml, root, scopes, logger } = params;
    const urlMap = new Map<string, string>();
    let bytesWritten = 0;

    for (const src of findHostedImageSrcs(bodyHtml)) {
        // hostedContents src can be relative in some API versions; resolve
        // against the message's own resource URL so the hash — and dedup — is
        // based on one canonical absolute URL regardless of how it was written.
        const base = `https://graph.microsoft.com/v1.0/chats/${chatId}/messages/${messageId}/`;
        const absoluteUrl = new URL(src, base).toString();
        const hash = await assetHash(absoluteUrl);
        const itemId = `${chatId}:asset:${hash}`;
        const alreadyDone = await isItemDone(runId, "teams", itemId);

        try {
            // The request still happens even when already done: the relative
            // path needs the file's extension, and Content-Type is only
            // available from the response headers. What's skipped is the body
            // read and disk write below — the expensive part on a resume.
            const response = await graphFetch(absoluteUrl, {
                scopes,
                service: "outlook"
            } satisfies GraphRequestOptions);

            const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim();
            const extension = (contentType && EXTENSION_BY_CONTENT_TYPE[contentType]) || ".bin";

            if (!alreadyDone) {
                const blob = await response.blob();
                const bytes = await writeFile(root, assetDiskPath(chatKey, hash, extension), blob);
                await putItem({ runId, module: "teams", itemId, status: "done", bytes });
                bytesWritten += bytes;
            }

            urlMap.set(src, assetRelativePath(hash, extension));
        } catch (err) {
            // One missing image should not fail the whole message — the
            // sanitizer drops any <img> without a urlMap entry, so the message
            // still renders, just without that picture.
            const message = err instanceof Error ? err.message : String(err);
            await logger.warn(`teams asset fetch failed chat=${chatId} msg=${messageId}: ${message}`);
        }
    }

    return { urlMap, bytes: bytesWritten };
}
